import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { outreachApi } from '../api/outreachApi';
import InfoHint from './InfoHint';
import MailboxPicker from './MailboxPicker';
import SpamCheckPanel from './SpamCheckPanel';

// Monday-first, matching Python's weekday numbers, which is what the
// schedule stores.
const DAYS = [0, 1, 2, 3, 4, 5, 6];

// Enough to cover where leads actually are; the field still accepts any
// IANA name the server will resolve.
const TIMEZONES = [
  'Europe/Kyiv', 'Europe/Berlin', 'Europe/London', 'Europe/Warsaw', 'Europe/Lisbon',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'America/Sao_Paulo',
  'Asia/Singapore', 'Asia/Dubai', 'Asia/Tokyo', 'Australia/Sydney', 'UTC',
];

export default function CampaignSettings({ campaign, accounts, onSaved, onClose }) {
  const { t } = useTranslation('common');

  const [name, setName] = useState(campaign.name);
  const [accountIds, setAccountIds] = useState(campaign.account_ids);
  const [schedule, setSchedule] = useState(campaign.schedule);
  const [guards, setGuards] = useState(campaign.guards);
  const [stopOn, setStopOn] = useState(campaign.stop_on);
  const [optOutLine, setOptOutLine] = useState(campaign.opt_out_line || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [checking, setChecking] = useState(false);

  const patchSchedule = (changes) => {
    setSaved(false);
    setSchedule({ ...schedule, ...changes });
  };

  const toggleDay = (day) =>
    patchSchedule({
      days: schedule.days.includes(day)
        ? schedule.days.filter((d) => d !== day)
        : [...schedule.days, day].sort((a, b) => a - b),
    });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await outreachApi.updateCampaign(campaign.id, {
        name,
        account_ids: accountIds,
        schedule,
        guards,
        stop_on: stopOn,
        opt_out_line: optOutLine,
      });
      setSaved(true);
      await onSaved(updated);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="campaign-wizard">
      <div className="import-wizard-head">
        <h2 className="unibox-detail-subject">{t('outreach.campaign.settingsTitle', { name: campaign.name })}</h2>
        <button className="unibox-page-button" onClick={onClose}>
          {t('outreach.close')}
        </button>
      </div>

      <div className="campaign-wizard-body">
        <div className="campaign-form">
          <div className="campaign-field">
            <label>{t('outreach.campaign.name')}</label>
            <input className="unibox-select" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <MailboxPicker
            accounts={accounts}
            selected={accountIds}
            onChange={(ids) => {
              setSaved(false);
              setAccountIds(ids);
            }}
            hint={t('outreach.hints.mailboxesLive')}
            t={t}
          />

          <div className="campaign-field">
            <label>
              {t('outreach.campaign.timezone')}
              <InfoHint text={t('outreach.hints.timezone')} />
            </label>
            <input
              className="unibox-select"
              list="campaign-timezones"
              value={schedule.timezone}
              onChange={(e) => patchSchedule({ timezone: e.target.value })}
            />
            <datalist id="campaign-timezones">
              {TIMEZONES.map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          </div>

          <div className="campaign-field">
            <label>{t('outreach.campaign.days')}</label>
            <div className="campaign-days">
              {DAYS.map((day) => (
                <label key={day} className="campaign-checkbox">
                  <input
                    type="checkbox"
                    checked={schedule.days.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                  {t(`outreach.campaign.weekdays.${day}`)}
                </label>
              ))}
            </div>
          </div>

          <label className="campaign-inline">
            {t('outreach.campaign.window')}
            <InfoHint text={t('outreach.hints.window')} />
            <input
              className="unibox-select campaign-number"
              type="time"
              value={schedule.window_start.slice(0, 5)}
              onChange={(e) => patchSchedule({ window_start: `${e.target.value}:00` })}
            />
            <input
              className="unibox-select campaign-number"
              type="time"
              value={schedule.window_end.slice(0, 5)}
              onChange={(e) => patchSchedule({ window_end: `${e.target.value}:00` })}
            />
          </label>

          <label className="campaign-inline">
            {t('outreach.campaign.minGap')}
            <InfoHint text={t('outreach.hints.minGap')} />
            <input
              className="unibox-select campaign-number"
              type="number"
              min="0"
              value={schedule.min_gap_seconds / 60}
              onChange={(e) => patchSchedule({ min_gap_seconds: Number(e.target.value) * 60 })}
            />
          </label>

          <label className="campaign-inline">
            {t('outreach.campaign.jitter')}
            <InfoHint text={t('outreach.hints.jitter')} />
            <input
              className="unibox-select campaign-number"
              type="number"
              min="0"
              value={schedule.jitter_seconds / 60}
              onChange={(e) => patchSchedule({ jitter_seconds: Number(e.target.value) * 60 })}
            />
          </label>

          <label className="campaign-inline">
            {t('outreach.campaign.dailyCap')}
            <InfoHint text={t('outreach.hints.campaignDailyCap')} />
            <input
              className="unibox-select campaign-number"
              type="number"
              min="1"
              placeholder={t('outreach.campaign.dailyCapUnset')}
              value={schedule.daily_cap_total ?? ''}
              onChange={(e) =>
                patchSchedule({ daily_cap_total: e.target.value ? Number(e.target.value) : null })
              }
            />
          </label>

          <label className="campaign-inline">
            {t('outreach.campaign.maxBounceRate')}
            <InfoHint text={t('outreach.hints.maxBounceRate')} />
            <input
              className="unibox-select campaign-number"
              type="number"
              min="1"
              max="50"
              value={Math.round(guards.max_bounce_rate * 100)}
              onChange={(e) => setGuards({ ...guards, max_bounce_rate: Number(e.target.value) / 100 })}
            />
          </label>

          <label className="campaign-checkbox">
            <input
              type="checkbox"
              checked={guards.auto_pause}
              onChange={() => setGuards({ ...guards, auto_pause: !guards.auto_pause })}
            />
            {t('outreach.campaign.autoPause')}
            <InfoHint text={t('outreach.hints.autoPause')} />
          </label>

          <div className="campaign-field">
            <label>{t('outreach.campaign.stopOnHeading')}</label>
            {['reply', 'ooo', 'click'].map((key) => (
              <label key={key} className="campaign-checkbox">
                <input
                  type="checkbox"
                  checked={stopOn[key]}
                  onChange={() => setStopOn({ ...stopOn, [key]: !stopOn[key] })}
                />
                {t(`outreach.campaign.stopOn.${key}`)}
              </label>
            ))}
          </div>

          <div className="campaign-field">
            <label>
              {t('outreach.campaign.optOutLine')}
              <InfoHint text={t('outreach.hints.optOut')} />
            </label>
            <input
              className="unibox-select"
              value={optOutLine}
              onChange={(e) => setOptOutLine(e.target.value)}
            />
          </div>
        </div>

        <aside className="campaign-preview">
          <h3 className="campaign-preview-title">{t('outreach.campaign.stepsReadOnly')}</h3>
          <p className="unibox-reply-hint">{t('outreach.campaign.stepsReadOnlyHint')}</p>
          {campaign.steps.map((step) => (
            <div key={step.index} className="campaign-step-readonly">
              <strong>
                {t('outreach.campaign.step', { number: step.index + 1 })}
                {step.index > 0 && ` · ${t('outreach.campaign.afterDays', { days: step.delay_days })}`}
                {step.same_thread && ` · ${t('outreach.campaign.sameThread')}`}
              </strong>
              <div className="campaign-step-subject">{step.variants[0].subject}</div>
              <div className="campaign-step-body">{step.variants[0].body}</div>
            </div>
          ))}

          <button className="unibox-refresh" onClick={() => setChecking(true)}>
            <ShieldCheck size={14} /> {t('outreach.check.short')}
          </button>
        </aside>
      </div>

      <div className="campaign-wizard-footer">
        <button className="unibox-button" onClick={save} disabled={busy || accountIds.length === 0}>
          {busy ? t('outreach.campaign.saving') : t('outreach.campaign.saveSettings')}
        </button>
        {saved && <span className="unibox-reply-hint">{t('outreach.campaign.settingsSaved')}</span>}
        {error && <span className="unibox-error">{String(error)}</span>}
      </div>

      {checking && (
        <SpamCheckPanel
          campaignId={campaign.id}
          stepCount={campaign.steps.length}
          onClose={() => setChecking(false)}
        />
      )}
    </div>
  );
}
